import type { ReactNode } from 'react';
import { Button } from '@dpem/design-system';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_AUTHENTICATED_PATH } from '@/router/paths';
import styles from './ErrorPage.module.css';

/**
 * One presentation for every dead end: forbidden, not found, and unexpected.
 *
 * Three rules, applied to all of them.
 *
 * **Say what to do next.** "403 Forbidden" tells a user nothing they can act on.
 * "Your roles do not include method review — ask an administrator" does.
 *
 * **Show the trace id when, and only when, support will need it.** A 404 needs no
 * trace id; a 500 is useless without one.
 *
 * **Never surface a raw exception.** A stack trace or a driver error message is a
 * disclosure, and the browser console already has whatever a developer needs.
 */

export interface ErrorPageProps {
  /** Short machine-ish label above the title: `403`, `NOT FOUND`, `SURFACE FAILED`. */
  code: string;
  title: string;
  detail: ReactNode;
  traceId?: string | null;
  /** Rendered after the built-in "back to workspace" link. */
  children?: ReactNode;
}

export function ErrorPage({ code, title, detail, traceId = null, children }: ErrorPageProps) {
  const navigate = useNavigate();

  return (
    <section className={styles.page} role="alert" aria-labelledby="error-page-title">
      <p className={styles.code}>{code}</p>
      <h1 className={styles.title} id="error-page-title">
        {title}
      </h1>
      <p className={styles.detail}>{detail}</p>

      {traceId !== null && traceId !== '' && (
        <div className={styles.trace}>
          <span className={styles.traceLabel}>Trace id</span>
          <span className={styles.traceValue}>{traceId}</span>
        </div>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={() => void navigate(DEFAULT_AUTHENTICATED_PATH)}>
          Back to workspace
        </Button>
        {children}
      </div>
    </section>
  );
}
